// @givmo/org-console — CONNECTED AI: what acts for this organization that is not a person.
//
// ══ WHY AN ORGANIZATION NEEDS ITS OWN LIST ═════════════════════════════════════════════════════
//
// A connected AI acts for the organization with the DELEGATING HUMAN's own live authority. Before
// this list existed, a connector one admin authorized was visible only on THAT person's own account
// page — and therefore invisible to the organization it acts for. So every row names the admin it
// acts as, and that adjacency is the point: the connector's authority is not its own.
//
// ⚠ `scopes` IS THE CONSENT-TIME CEILING, NOT LIVE AUTHORITY. What a connector may actually do is the
// delegating human's own effective permissions, re-resolved on every call. Rendering a scope list
// unqualified beside a row asserts a permission set that may not exist — so the list sits behind a
// disclosure with one sentence naming what it is. Hiding it entirely would be the opposite mistake:
// an organization could not see what it once agreed to.
//
// ⚠ NEVER ANY TOKEN MATERIAL. No bearer, no hash, no credential id, on any path. Nothing on the wire
// carries it and this panel must not synthesize an id-looking field either — reading this list is
// open to every admin of the organization, so a field here is a field every admin can see. Even the
// acting admin's id stays out of the DOM: the NAME is what a person can act on.
//
// ⚠ ENDING ONE HAS EXACTLY ONE NON-SUCCESS SHAPE, AND IT IS NOT A FAILURE. An unknown grant, another
// organization's grant, and an id that is not even well-formed all answer the same 404 — grant ids
// stay un-enumerable by design. So the confirm's error leg says "already ended, or not this
// organization's", never "failed".
//
// DATA CONTRACT: the `<prefix>-connected-ai-*` test-id family, rooted at `<prefix>-connected-ai`.

import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  ErrorMessage,
  Icon,
  testIdProp,
} from '@givmo/design-system';
import { Bot } from 'lucide-react';
import { testIdPrefix, type OrgConsoleConfig } from './org-console-config';
import { isOrgConsoleUnavailable } from './org-console-transport';
import { formatOrgEventTime } from './org-decode';
import { useOrgAccessGate, useOrgConnectedAi, useRevokeOrgConnectedAi } from './use-org-console';
import { OrgConsoleUnavailableNotice } from './org-console-notices';
import type { OrgConnectedAiRow } from './wire-types';

export interface OrgConnectedAiPanelProps {
  config: OrgConsoleConfig;
}

/** The whole sentence, as ruled. Used as the row's accessible name so it is never split in two. */
export function connectedAiSentence(row: OrgConnectedAiRow): string {
  return `Connected AI: ${row.client_name}, acting as ${row.acting_admin_name}`;
}

export function OrgConnectedAiPanel({ config }: OrgConnectedAiPanelProps) {
  const prefix = `${testIdPrefix(config)}-connected-ai`;
  const gate = useOrgAccessGate(config.orgRef);
  const list = useOrgConnectedAi(config.orgRef);
  const revoke = useRevokeOrgConnectedAi(config.orgRef, config.onAccessWritten);
  const [ending, setEnding] = useState<OrgConnectedAiRow | null>(null);
  const [alreadyGone, setAlreadyGone] = useState(false);
  const [failed, setFailed] = useState(false);

  const rows = useMemo(() => list.data ?? [], [list.data]);
  // PERMISSIVE, matching the roster's actions column: the server refuses a Member's revoke anyway,
  // while a restrictive gate would hide the control from a real Global Admin over a flaky read.
  const canRevoke = gate.isGlobalAdmin;

  // ⚠ ABSENT MEANS NOTHING ON SCREEN (OM-95). Where nobody can connect an AI to an organization (the
  // organization AI connections off, as on prod), the server answers this list with the family's
  // 404, and a section promising "when an admin connects an AI, it appears here" would promise an act
  // nobody can take. The rest of the page already says when the whole console is off.
  if (list.isError && isOrgConsoleUnavailable(list.error)) {
    return gate.isUnavailable ? (
      <OrgConsoleUnavailableNotice
        testId={`${prefix}-unavailable`}
        title="Connected AI"
        description="This environment doesn’t have the console turned on, so there is nothing connected to show yet."
      />
    ) : null;
  }

  const close = () => {
    setEnding(null);
    setAlreadyGone(false);
    setFailed(false);
  };

  const confirm = () => {
    if (ending === null) return;
    setAlreadyGone(false);
    setFailed(false);
    revoke.mutate(
      { grantId: ending.id },
      {
        onSuccess: close,
        onError: (error: unknown) => {
          // The ONE non-success shape. It is not an error and must not read as one.
          if (isOrgConsoleUnavailable(error)) {
            setAlreadyGone(true);
            return;
          }
          setFailed(true);
        },
      },
    );
  };

  return (
    // The section's root carries the family's bare prefix, as the roster's and the invites' do, so a
    // host page can pin which sections it mounts and in what order.
    <Card testId={prefix}>
      <CardBody className="flex flex-col gap-16">
        <div>
          <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            Connected AI
          </h2>
          <p className="mt-4 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            {`Each one acts for this ${config.vocab.orgNoun} with the live permissions of the admin it acts as, checked again every time it does anything.`}
          </p>
        </div>

        {list.isPending && (
          <p className="font-sans text-s13 text-text-tertiary" {...testIdProp(`${prefix}-loading`)}>
            Loading…
          </p>
        )}

        {list.isError && !isOrgConsoleUnavailable(list.error) && (
          <ErrorMessage testId={`${prefix}-error`}>
            We couldn’t load what is connected here.
          </ErrorMessage>
        )}

        {!list.isPending && rows.length === 0 && !list.isError && (
          <EmptyState
            testId={`${prefix}-empty-state`}
            icon={<Icon icon={Bot} size={20} aria-hidden />}
            title="Nothing is connected"
            description={`When an admin connects an AI to this ${config.vocab.orgNoun}, it appears here.`}
          />
        )}

        {rows.length > 0 && (
          <ul className="flex flex-col gap-8">
            {rows.map((row) => (
              <ConnectorRow
                key={row.id}
                row={row}
                prefix={prefix}
                canRevoke={canRevoke}
                onEnd={() => {
                  setAlreadyGone(false);
                  setFailed(false);
                  setEnding(row);
                }}
              />
            ))}
          </ul>
        )}

        {list.hasNextPage && (
          <div>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              disabled={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
              testId={`${prefix}-load-more`}
            >
              {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </CardBody>

      <Dialog.Root
        open={ending !== null}
        onOpenChange={(open) => !open && !revoke.isPending && close()}
      >
        <Dialog.Content
          aria-label={ending === null ? 'End a connection' : `End ${ending.client_name}`}
          className="flex flex-col gap-16"
          testId={`${prefix}-confirm`}
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {ending === null ? 'End this connection?' : `End ${ending.client_name}?`}
          </Dialog.Title>
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {ending === null
              ? ''
              : `It stops acting for this ${config.vocab.orgNoun} straight away, including anything it is in the middle of. ${ending.acting_admin_name} can connect it again.`}
          </p>

          {alreadyGone && (
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp(`${prefix}-confirm-gone`)}
            >
              {`This connection has already ended, or it does not belong to this ${config.vocab.orgNoun}. There is nothing left to do.`}
            </p>
          )}
          {failed && (
            <ErrorMessage testId={`${prefix}-confirm-error`}>
              We couldn’t end this just now. Nothing changed — please try again.
            </ErrorMessage>
          )}

          <div className="mt-8 flex items-center justify-end gap-8">
            <Dialog.Close
              disabled={revoke.isPending}
              render={<Button appearance="saas" tone="secondary" size="md" />}
              {...testIdProp(`${prefix}-confirm-cancel`)}
            >
              {alreadyGone ? 'Close' : 'Cancel'}
            </Dialog.Close>
            {!alreadyGone && (
              <Button
                appearance="saas"
                tone="primary"
                size="md"
                loading={revoke.isPending}
                onClick={confirm}
                testId={`${prefix}-confirm-submit`}
              >
                End it
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </Card>
  );
}

interface ConnectorRowProps {
  row: OrgConnectedAiRow;
  prefix: string;
  canRevoke: boolean;
  onEnd: () => void;
}

function ConnectorRow({ row, prefix, canRevoke, onEnd }: ConnectorRowProps) {
  const [showScopes, setShowScopes] = useState(false);
  const id = `${prefix}-row-${row.id}`;
  const linked = formatOrgEventTime(row.linked_at);
  const active = formatOrgEventTime(row.last_active_at);

  return (
    <li
      className="flex flex-col gap-12 rounded-lg border border-border-subtle p-16"
      aria-label={connectedAiSentence(row)}
      {...testIdProp(id)}
    >
      <div className="flex items-start justify-between gap-16">
        <div className="min-w-0">
          <p className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
            {row.client_name}
          </p>
          {/* The connector's authority is not its own, so the admin it borrows it from is the row's
              subtitle rather than a detail behind a click. */}
          <p
            className="mt-2 truncate font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
            {...testIdProp(`${id}-acting`)}
          >
            acting as {row.acting_admin_name}
          </p>
          {linked.length > 0 && (
            // Each half is its own inline block, capped at the column: it stays whole wherever it
            // fits on a line of its own, so a narrow row wraps BETWEEN the halves, never inside
            // "last seen" (which used to strand "last" at a phone width's line end); where a half
            // alone is wider than the column (320px, beside End) it wraps inside itself rather
            // than running out of the column. The separator ends the first half, so it can never
            // sit alone on a line.
            <p
              className="mt-2 font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
              {...testIdProp(`${id}-times`)}
            >
              <span className="inline-block max-w-full">
                Connected {linked}
                {active.length > 0 ? ' ·' : ''}
              </span>
              {active.length > 0 ? (
                <>
                  {' '}
                  <span className="inline-block max-w-full">last seen {active}</span>
                </>
              ) : null}
            </p>
          )}
        </div>
        {canRevoke && (
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={onEnd}
            testId={`${id}-revoke`}
          >
            End
          </Button>
        )}
      </div>

      {row.scopes.length > 0 && (
        <div>
          <button
            type="button"
            className="font-sans text-s12 font-w600 tracking-givmo text-brand-600 underline underline-offset-2 outline-none focus-visible:shadow-ring"
            aria-expanded={showScopes}
            onClick={() => setShowScopes((current) => !current)}
            {...testIdProp(`${id}-scopes-toggle`)}
          >
            {showScopes ? 'Hide what it agreed to' : 'What it agreed to'}
          </button>
          {showScopes && (
            <div className="mt-8 flex flex-col gap-8">
              {/* ⚠ The ONE sentence that keeps the list below honest. Without it the list reads as a
                  statement of what the connector can do right now, which is a different — and
                  possibly untrue — claim. */}
              <p
                className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                {...testIdProp(`${id}-scopes-ceiling`)}
              >
                {`This is the most it agreed to at the time it was connected — never more. What it can actually do is whatever ${row.acting_admin_name} can do, checked again every time.`}
              </p>
              <ul className="flex flex-wrap gap-8" {...testIdProp(`${id}-scopes`)}>
                {row.scopes.map((scope) => (
                  <li
                    key={scope}
                    className="rounded-md bg-surface-sunken px-8 py-4 font-sans text-s12 tracking-givmo text-text-secondary"
                  >
                    {scope}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
